import { DatePipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckoutShippingMethodsComponentDef } from '@df/checkout/checkout-shipping-methods.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ICmsConfigResource } from '@icms/core/resource/config/icms-config.resource';
import { SurchargeInfoBlockComponent } from '../basket/surcharge-info/surcharge-info-block.component';
import { CMS_CONFIG_SCURRI } from '../cms/config/scurri.cms-config';
import { IfNotTradeStoreDirective } from '../multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from '../multisite/trade/if-trade-store.directive';
import { InputWrapBodyDirective } from '../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../ui/form/input-wrap.component';
import { RadioComponent } from '../ui/form/radio';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface IShippingMethod {
            surcharge?: string;
            estimated_delivery_data?: {
                dates: string[];
            };
        }
    }
}

@Component({
    selector: 'checkout-shipping-methods',
    templateUrl: './checkout-shipping-methods.html',
    standalone: true,
    imports: [
        FormsModule,
        IfNotTradeStoreDirective,
        IfTradeStoreDirective,
        InputWrapComponent,
        InputWrapBodyDirective,
        MuiInputDirective,
        NgClass,
        PriceComponent,
        RadioComponent,
        RadioGroupDirective,
        SurchargeInfoBlockComponent,
        TranslationModule
    ],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutShippingMethodsComponent extends CheckoutShippingMethodsComponentDef implements OnInit {
    private translationService = inject(TranslationService);
    private datePipe = inject(DatePipe);
    private cmsConfigResource = inject(ICmsConfigResource);
    protected canDisplayScurriEstimatedDelivery = false;

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadScurriConfig();
    }

    hasFreeShipping(methodPrice: number): boolean {
        if (methodPrice === 0) {
            return true;
        }
        return false;
    }

    protected async loadScurriConfig(): Promise<void> {
        this.canDisplayScurriEstimatedDelivery = (
            await this.cmsConfigResource.loadModelByUrl(CMS_CONFIG_SCURRI)
        )?.data?.content?.enabledEstimatedDelivery;
        this.detectChanges();
    }

    protected getEstimatedDate(method: Checkout.IShippingMethod): string | undefined {
        if (method.code === 'tcoshippingmethodtableratescurri_DPD|Next Day 12') {
            return undefined;
        }

        if (!method.estimated_delivery_data) {
            return undefined;
        }
        if (method.estimated_delivery_data.dates?.length > 1) {
            const startDate = new Date(method.estimated_delivery_data.dates[0]);
            const endDate = new Date(method.estimated_delivery_data.dates[method.estimated_delivery_data.dates.length - 1]);
            return startDate && endDate
                ? this.translationService.transform(
                      'Between % and %, % %',
                      this.datePipe.transform(startDate, 'EEEE'),
                      this.datePipe.transform(endDate, 'EEEE'),
                      `${this.datePipe.transform(startDate, 'dd')}-${this.datePipe.transform(endDate, 'dd')}`,
                      this.datePipe.transform(endDate, 'MMM')
                  )
                : undefined;
        } else if (method.estimated_delivery_data.dates?.length === 1) {
            const deliveryDate = new Date(method.estimated_delivery_data.dates[0]);

            const dayName = this.datePipe.transform(deliveryDate, 'EEEE');
            const dateFormatted = this.datePipe.transform(deliveryDate, 'dd');
            const monthFormatted = this.datePipe.transform(deliveryDate, 'MMM');

            return this.translationService.transform(`Estimated delivery %, % %`, dayName, dateFormatted, monthFormatted);
        }
    }

    protected get showSurchargeInfo(): boolean {
        return !!this.methods.find(el => el.surcharge);
    }

    protected readonly info =
        'Sorry, there is no shipping option available at the moment. Please contact our customer services team on +442380814360 who will be happy to help';
}
